/**
 * Modal 640 px "Nuevo patrón" / "Editar patrón" (008 · T087 · US5; contracts/ui.md §1.4, spec US5
 * escenarios 1–3).
 *
 * Nombre, tipo ("Semanal" alineado a lu…do, o "Ciclo de N días" con N 2–56) y la grilla de días del
 * ciclo: cada día elige "Descanso" o un turno, y opcionalmente un segundo turno (hasta 2). Debajo,
 * la vista previa de 14 días desde hoy. La validación es la del contrato
 * (`SchedulePatternBodySchema`/`PatternDaySchema`); los solapes entre turnos de un día o con el día
 * siguiente del ciclo los valida el servidor y vuelven al día (`days.N.template_ids`).
 *
 * Al editar, tipo y N son inmutables (`UpdateSchedulePatternBody` solo admite nombre y días) y se
 * envía solo lo que cambió; sin cambios no se llama a la API.
 */
import { useMemo, useState } from 'react';

import {
  PATTERN_MAX_CYCLE_DAYS,
  SchedulePatternBodySchema,
  type PatternDay,
  type SchedulePattern,
  type SchedulePatternKind,
  type ShiftTemplate,
  type UpdateSchedulePatternBody,
} from '@fermin/contracts';

import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { Field } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { Select, type SelectOption } from '../../../components/ui/select';
import { useToast } from '../../../components/ui/toast';
import { t } from '../../../i18n';
import { readOnlyProps, useReadOnly } from '../../shell/use-read-only';
import {
  isStatusConflict,
  scheduleErrorMessage,
  useCreateSchedulePattern,
  useShiftTemplates,
  useUpdateSchedulePattern,
} from '../api';
import { timeRange, weekdayLong } from '../format';

import { PatternPreview, type PreviewPatternDay, type PreviewTemplate } from './pattern-preview';

import type { ApiError } from '../../../api/client';

/** Valor del primer selector de un día que significa "Descanso". */
export const REST = 'REST';
/** Valor del segundo selector que significa "sin segundo turno". */
export const NO_SECOND = 'NONE';
/** N mínimo de un ciclo libre en la UI (contracts/ui.md §1.4: "N (2–56)"). */
export const CYCLE_MIN_DAYS = 2;

export interface EditorDay {
  /** `REST` o el id de la plantilla. */
  first: string;
  /** `NONE` o el id de la segunda plantilla. */
  second: string;
}

export interface PatternEditorValues {
  name: string;
  kind: SchedulePatternKind;
  cycleDays: string;
  days: EditorDay[];
}

interface EditorErrors {
  name?: string;
  cycleDays?: string;
  days?: string;
  byDay: Record<number, string>;
}

const NO_ERRORS: EditorErrors = { byDay: {} };

function restDays(count: number): EditorDay[] {
  return Array.from({ length: count }, () => ({ first: REST, second: NO_SECOND }));
}

/** Valores del formulario desde un patrón existente (o vacíos: semanal, todo descanso). */
export function editorValuesFrom(pattern?: SchedulePattern): PatternEditorValues {
  if (!pattern) {
    return { name: '', kind: 'WEEKLY', cycleDays: '7', days: restDays(7) };
  }
  const days = restDays(pattern.cycle_days);
  for (const day of pattern.days) {
    if (day.day_index < days.length && day.kind === 'SHIFT') {
      days[day.day_index] = {
        first: day.template_ids[0] ?? REST,
        second: day.template_ids[1] ?? NO_SECOND,
      };
    }
  }
  return { name: pattern.name, kind: pattern.kind, cycleDays: String(pattern.cycle_days), days };
}

/** Días del cuerpo (`PatternDay[]`) desde la grilla del editor. */
export function patternDaysFrom(days: readonly EditorDay[]): PatternDay[] {
  return days.map((day, index) =>
    day.first === REST
      ? { day_index: index, kind: 'REST', template_ids: [] }
      : {
          day_index: index,
          kind: 'SHIFT',
          template_ids: day.second !== NO_SECOND ? [day.first, day.second] : [day.first],
        },
  );
}

/** Ajusta la grilla a `count` días conservando los ya definidos (los nuevos, descanso). */
export function resizeDays(days: readonly EditorDay[], count: number): EditorDay[] {
  if (count <= days.length) {
    return days.slice(0, count);
  }
  return [...days, ...restDays(count - days.length)];
}

/** Índice de día de un path de error (`days.3.template_ids` o `['days', 3, …]`). */
export function dayIndexFromPath(path: unknown): number | null {
  if (Array.isArray(path)) {
    return path[0] === 'days' && typeof path[1] === 'number' ? path[1] : null;
  }
  if (typeof path !== 'string') {
    return null;
  }
  const match = /^days(?:\.|\[)(\d+)/.exec(path);
  return match ? Number(match[1]) : null;
}

/** Errores de la API: días (`days.N…`), nombre (issue o `409 CONFLICT field=name`). */
export function patternApiErrors(error: ApiError): EditorErrors | null {
  if (error.is('CONFLICT') && error.details?.['field'] === 'name') {
    return { name: t('schedules.patterns.editor.errors.nameTaken'), byDay: {} };
  }
  const issues = error.details?.['issues'];
  if (!error.is('VALIDATION_ERROR') || !Array.isArray(issues)) {
    return null;
  }
  const errors: EditorErrors = { byDay: {} };
  let any = false;
  for (const issue of issues as { path?: unknown; message?: unknown }[]) {
    const message = typeof issue.message === 'string' ? issue.message : error.message;
    const index = dayIndexFromPath(issue.path);
    if (index !== null) {
      errors.byDay[index] ??= message;
      any = true;
      continue;
    }
    const field = typeof issue.path === 'string' ? issue.path.split('.')[0] : undefined;
    if (field === 'name') {
      errors.name ??= message;
      any = true;
    } else if (field === 'cycle_days') {
      errors.cycleDays ??= message;
      any = true;
    } else if (field === 'days') {
      errors.days ??= message;
      any = true;
    }
  }
  return any ? errors : null;
}

/** "M · Mañana 08:00–17:00". */
function templateOptionLabel(template: PreviewTemplate): string {
  return `${template.code} · ${template.name} ${timeRange(template.start_time, template.end_time)}`;
}

export interface PatternEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Patrón a editar; ausente = alta. */
  pattern?: SchedulePattern | undefined;
  /** Hoy en la zona de la empresa: inicio de la vista previa. */
  today: string;
}

/** El editor se monta al abrir (y por patrón): cada apertura parte del patrón o vacío. */
export function PatternEditorDialog({
  open,
  onOpenChange,
  pattern,
  today,
}: PatternEditorDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open ? (
        <PatternEditorContent
          key={pattern?.id ?? 'new'}
          onOpenChange={onOpenChange}
          pattern={pattern}
          today={today}
        />
      ) : null}
    </Dialog>
  );
}

function PatternEditorContent({
  onOpenChange,
  pattern,
  today,
}: Omit<PatternEditorDialogProps, 'open'>) {
  const { toast } = useToast();
  const { readOnly } = useReadOnly();
  const create = useCreateSchedulePattern();
  const update = useUpdateSchedulePattern();
  const templates = useShiftTemplates('ACTIVE');
  const isEdit = pattern !== undefined;
  const pending = create.isPending || update.isPending;

  const [values, setValues] = useState<PatternEditorValues>(() => editorValuesFrom(pattern));
  const [errors, setErrors] = useState<EditorErrors>(NO_ERRORS);

  // Plantillas activas + las que ya usa el patrón (pueden estar archivadas).
  const templateMap = useMemo(() => {
    const map = new Map<string, PreviewTemplate>();
    for (const day of pattern?.days ?? []) {
      day.shifts.forEach((shift) => map.set(shift.template_id, shift));
    }
    for (const template of templates.data ?? ([] as ShiftTemplate[])) {
      map.set(template.id, {
        code: template.code,
        name: template.name,
        start_time: template.current.start_time,
        end_time: template.current.end_time,
      });
    }
    return map;
  }, [pattern, templates.data]);

  const templateOptions: SelectOption[] = useMemo(
    () =>
      [...templateMap].map(([id, template]) => ({
        value: id,
        label: templateOptionLabel(template),
      })),
    [templateMap],
  );
  const firstOptions: SelectOption[] = useMemo(
    () => [{ value: REST, label: t('schedules.patterns.editor.rest') }, ...templateOptions],
    [templateOptions],
  );

  const kindOptions: SelectOption[] = useMemo(
    () => [
      { value: 'WEEKLY', label: t('schedules.patterns.kind.WEEKLY') },
      { value: 'CYCLE', label: t('schedules.patterns.editor.kindCycle') },
    ],
    [],
  );

  const cycleDays = values.days.length;
  const previewDays: PreviewPatternDay[] = useMemo(
    () =>
      patternDaysFrom(values.days).map((day) => ({
        kind: day.kind,
        template_ids: day.template_ids,
      })),
    [values.days],
  );

  function setKind(kind: SchedulePatternKind) {
    setValues((current) => ({
      ...current,
      kind,
      ...(kind === 'WEEKLY' ? { cycleDays: '7', days: resizeDays(current.days, 7) } : {}),
    }));
  }

  function setCycleDays(text: string) {
    const count = Number(text);
    setValues((current) => ({
      ...current,
      cycleDays: text,
      ...(Number.isInteger(count) && count >= 1 && count <= PATTERN_MAX_CYCLE_DAYS
        ? { days: resizeDays(current.days, count) }
        : {}),
    }));
  }

  function setDay(index: number, patch: Partial<EditorDay>) {
    setValues((current) => ({
      ...current,
      days: current.days.map((day, i) => {
        if (i !== index) {
          return day;
        }
        const next = { ...day, ...patch };
        // Descanso no lleva segundo turno; el segundo no puede repetir el primero.
        if (next.first === REST || next.second === next.first) {
          next.second = NO_SECOND;
        }
        return next;
      }),
    }));
  }

  function onApiError(error: ApiError) {
    const mapped = patternApiErrors(error);
    if (mapped) {
      setErrors(mapped);
      return;
    }
    toast({
      title: isStatusConflict(error)
        ? t('schedules.patterns.errors.archived')
        : scheduleErrorMessage(error),
      variant: 'error',
    });
  }

  function validateCycle(): string | undefined {
    if (values.kind !== 'CYCLE') {
      return undefined;
    }
    const count = Number(values.cycleDays);
    if (
      !Number.isInteger(count) ||
      count < CYCLE_MIN_DAYS ||
      count > PATTERN_MAX_CYCLE_DAYS ||
      count !== values.days.length
    ) {
      return t('schedules.patterns.editor.errors.cycleDays', {
        min: CYCLE_MIN_DAYS,
        max: PATTERN_MAX_CYCLE_DAYS,
      });
    }
    return undefined;
  }

  function submit() {
    const cycleError = isEdit ? undefined : validateCycle();
    const parsed = SchedulePatternBodySchema.safeParse({
      name: values.name,
      kind: values.kind,
      cycle_days: cycleDays,
      days: patternDaysFrom(values.days),
    });
    if (!parsed.success || cycleError) {
      const next: EditorErrors = { byDay: {}, ...(cycleError ? { cycleDays: cycleError } : {}) };
      for (const issue of parsed.success ? [] : parsed.error.issues) {
        const index = dayIndexFromPath(issue.path);
        if (index !== null) {
          next.byDay[index] ??= issue.message;
        } else if (issue.path[0] === 'name') {
          next.name ??= t('schedules.patterns.editor.errors.name');
        } else if (issue.path[0] === 'cycle_days') {
          next.cycleDays ??= issue.message;
        } else if (issue.path[0] === 'days') {
          next.days ??= issue.message;
        }
      }
      setErrors(next);
      return;
    }
    setErrors(NO_ERRORS);

    if (isEdit) {
      const body: UpdateSchedulePatternBody = {};
      if (parsed.data.name !== pattern.name) {
        body.name = parsed.data.name;
      }
      const before = JSON.stringify(patternDaysFrom(editorValuesFrom(pattern).days));
      if (JSON.stringify(parsed.data.days) !== before) {
        body.days = parsed.data.days;
      }
      if (Object.keys(body).length === 0) {
        onOpenChange(false);
        return;
      }
      update.mutate(
        { id: pattern.id, body },
        {
          onSuccess: (saved) => {
            toast({
              title: t('schedules.patterns.toast.updated', { name: saved.name }),
              variant: 'success',
            });
            onOpenChange(false);
          },
          onError: onApiError,
        },
      );
      return;
    }

    create.mutate(parsed.data, {
      onSuccess: (saved) => {
        toast({
          title: t('schedules.patterns.toast.created', { name: saved.name }),
          variant: 'success',
        });
        onOpenChange(false);
      },
      onError: onApiError,
    });
  }

  return (
    <DialogContent
      size="detail"
      title={
        isEdit
          ? t('schedules.patterns.editor.editTitle', { name: pattern.name })
          : t('schedules.patterns.editor.createTitle')
      }
      description={t('schedules.patterns.editor.description')}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            type="submit"
            form="pattern-editor-form"
            loading={pending}
            loadingText={t('common.states.saving')}
            {...readOnlyProps(readOnly)}
          >
            {isEdit ? t('common.buttons.save') : t('schedules.patterns.editor.submit')}
          </Button>
        </>
      }
    >
      <form
        id="pattern-editor-form"
        data-testid="pattern-editor"
        className="flex max-h-[65vh] flex-col gap-fm-4 overflow-y-auto pr-fm-1"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Field label={t('schedules.patterns.editor.name')} required error={errors.name}>
          {(control) => (
            <Input
              {...control}
              autoComplete="off"
              maxLength={60}
              value={values.name}
              onChange={(event) => setValues((v) => ({ ...v, name: event.target.value }))}
            />
          )}
        </Field>

        <div className="grid grid-cols-[1fr_160px] gap-fm-3">
          <Field
            label={t('schedules.patterns.editor.kind')}
            required
            help={
              isEdit
                ? t('schedules.patterns.editor.kindLocked')
                : values.kind === 'WEEKLY'
                  ? t('schedules.patterns.editor.weeklyHelp')
                  : t('schedules.patterns.editor.cycleHelp')
            }
          >
            {(control) => (
              <Select
                id={control.id}
                aria-describedby={control['aria-describedby']}
                value={values.kind}
                disabled={isEdit}
                onValueChange={(value) => setKind(value === 'CYCLE' ? 'CYCLE' : 'WEEKLY')}
                options={kindOptions}
              />
            )}
          </Field>
          {values.kind === 'CYCLE' ? (
            <Field
              label={t('schedules.patterns.editor.cycleDays')}
              required
              error={errors.cycleDays}
            >
              {(control) => (
                <Input
                  {...control}
                  type="number"
                  inputMode="numeric"
                  min={CYCLE_MIN_DAYS}
                  max={PATTERN_MAX_CYCLE_DAYS}
                  step={1}
                  disabled={isEdit}
                  value={values.cycleDays}
                  onChange={(event) => setCycleDays(event.target.value)}
                />
              )}
            </Field>
          ) : null}
        </div>

        <fieldset className="flex flex-col gap-fm-2">
          <legend className="mb-fm-1 text-fm-sm font-medium text-fm-text">
            {t('schedules.patterns.editor.days', { count: cycleDays })}
          </legend>
          {errors.days ? (
            <p role="alert" className="text-fm-xs text-fm-danger-text">
              {errors.days}
            </p>
          ) : null}
          <ol className="flex flex-col gap-fm-2">
            {values.days.map((day, index) => {
              const label =
                values.kind === 'WEEKLY'
                  ? weekdayLong(index + 1)
                  : t('schedules.patterns.editor.dayN', { n: index + 1 });
              const dayError = errors.byDay[index];
              return (
                <li
                  key={index}
                  data-testid="pattern-editor-day"
                  data-index={index}
                  className="grid grid-cols-[110px_1fr_1fr] items-start gap-fm-2"
                >
                  <span className="flex min-h-fm-control-sm items-center text-fm-sm font-medium">
                    {label}
                  </span>
                  <div className="flex flex-col gap-fm-1">
                    <Select
                      aria-label={t('schedules.patterns.editor.firstLabel', { day: label })}
                      invalid={dayError !== undefined}
                      value={day.first}
                      onValueChange={(first) => setDay(index, { first })}
                      options={firstOptions}
                    />
                    {dayError ? (
                      <p className="text-fm-xs text-fm-danger-text" role="alert">
                        {dayError}
                      </p>
                    ) : null}
                  </div>
                  {day.first !== REST ? (
                    <Select
                      aria-label={t('schedules.patterns.editor.secondLabel', { day: label })}
                      value={day.second}
                      onValueChange={(second) => setDay(index, { second })}
                      options={[
                        { value: NO_SECOND, label: t('schedules.patterns.editor.noSecond') },
                        ...templateOptions.filter((option) => option.value !== day.first),
                      ]}
                    />
                  ) : (
                    <span aria-hidden="true" />
                  )}
                </li>
              );
            })}
          </ol>
        </fieldset>

        <section className="flex flex-col gap-fm-2">
          <h3 className="text-fm-sm font-semibold text-fm-text">
            {t('schedules.patterns.preview.title')}
          </h3>
          <p className="text-fm-xs text-fm-text-secondary">
            {values.kind === 'WEEKLY'
              ? t('schedules.patterns.preview.weeklyHint')
              : t('schedules.patterns.preview.cycleHint')}
          </p>
          <PatternPreview
            kind={values.kind}
            cycleDays={cycleDays}
            days={previewDays}
            startDay={today}
            templates={templateMap}
          />
        </section>
      </form>
    </DialogContent>
  );
}
